import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import LoginPage from "./modules/auth/pages/LoginPage";
import { MedicinesCatalogProvider } from "./modules/medicines/context/MedicinesCatalogContext";
import { OrdersCatalogProvider } from "./modules/orders/context/OrdersCatalogContext";
import PublicAuthPage from "./modules/public/pages/PublicAuthPage";
import PublicCategoryPage from "./modules/public/pages/PublicCategoryPage";
import PublicCheckoutPage from "./modules/public/pages/PublicCheckoutPage";
import PublicHomePage from "./modules/public/pages/PublicHomePage";
import PublicProductsPage from "./modules/public/pages/PublicProductsPage";
import PublicVerifyCodePage from "./modules/public/pages/PublicVerifyCodePage";
import PrivateLayout from "./shared/layouts/PrivateLayout";
import { CartItem, PublicDeliveryInfo } from "./modules/public/types/store";
import { User } from "./types/auth";
import { PageMeta } from "./shared/seo/PageMeta";
import "./App.css";

type PublicUserSession = {
  phone: string;
  name: string;
  address: string;
  reference: string;
};

type RegisteredPublicUser = {
  names: string;
  lastNames: string;
  phone: string;
  email: string;
  birthDate: string;
  gender: string;
  address?: string;
  reference?: string;
};

const PLACEHOLDER_PUBLIC_USER: RegisteredPublicUser = {
  names: "Pablo",
  lastNames: "Perez Maguina",
  phone: "987654654",
  email: "pablo.perez@example.com",
  birthDate: "1990-01-15",
  gender: "Masculino",
  address: "Av 28 de Julio #123",
  reference: "Al costado de la tienda PIONEER, por la escalera",
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ADMIN_SESSION_KEY = "boticlick_admin_session";
const PUBLIC_SESSION_KEY = "boticlick_public_session";
const PENDING_PUBLIC_SESSION_KEY = "boticlick_pending_public_session";
const REGISTERED_USERS_KEY = "boticlick_registered_users";

type StoredSession<T> = {
  value: T;
  expiresAt: number;
};

function loadSession<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredSession<T>;
    if (!parsed?.value || typeof parsed.expiresAt !== "number") {
      localStorage.removeItem(key);
      return null;
    }
    if (Date.now() > parsed.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function saveSession<T>(key: string, value: T): void {
  const payload: StoredSession<T> = {
    value,
    expiresAt: Date.now() + ONE_DAY_MS,
  };
  localStorage.setItem(key, JSON.stringify(payload));
}

function loadPendingPublicSession(): PublicUserSession | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PUBLIC_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PublicUserSession;
    if (!parsed?.phone) {
      sessionStorage.removeItem(PENDING_PUBLIC_SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(PENDING_PUBLIC_SESSION_KEY);
    return null;
  }
}

function savePendingPublicSession(user: PublicUserSession): void {
  sessionStorage.setItem(PENDING_PUBLIC_SESSION_KEY, JSON.stringify(user));
}

function clearPendingPublicSession(): void {
  sessionStorage.removeItem(PENDING_PUBLIC_SESSION_KEY);
}

function loadRegisteredUsers(): Record<string, RegisteredPublicUser> {
  try {
    const raw = localStorage.getItem(REGISTERED_USERS_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, RegisteredPublicUser>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveRegisteredUser(user: RegisteredPublicUser): void {
  const users = loadRegisteredUsers();
  users[user.phone] = user;
  localStorage.setItem(REGISTERED_USERS_KEY, JSON.stringify(users));
}

function buildPublicSessionFromRegistered(
  user: RegisteredPublicUser,
): PublicUserSession {
  return {
    phone: user.phone,
    name: `${user.names} ${user.lastNames}`.trim(),
    address: user.address?.trim() ?? "",
    reference: user.reference?.trim() ?? "",
  };
}

function buildSimulatedPublicSession(phone: string): PublicUserSession {
  const registered = loadRegisteredUsers()[phone];
  if (registered) {
    return buildPublicSessionFromRegistered(registered);
  }

  if (phone === PLACEHOLDER_PUBLIC_USER.phone) {
    return buildPublicSessionFromRegistered(PLACEHOLDER_PUBLIC_USER);
  }

  return {
    phone,
    name: "Usuario",
    address: "",
    reference: "",
  };
}

export default function App() {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState<User | null>(() =>
    loadSession<User>(ADMIN_SESSION_KEY),
  );
  const [pendingPublicUser, setPendingPublicUser] =
    useState<PublicUserSession | null>(() => loadPendingPublicSession());
  const [publicUser, setPublicUser] = useState<PublicUserSession | null>(() =>
    loadSession<PublicUserSession>(PUBLIC_SESSION_KEY),
  );
  const [publicValidated, setPublicValidated] = useState<boolean>(
    () => !!loadSession<PublicUserSession>(PUBLIC_SESSION_KEY),
  );
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const handleLogin = (loggedUser: User) => {
    setAdminUser(loggedUser);
    saveSession<User>(ADMIN_SESSION_KEY, loggedUser);
    navigate("/admin");
  };

  const handleLogout = () => {
    setAdminUser(null);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    navigate("/admin/login");
  };

  const handlePhoneSubmit = (phone: string) => {
    localStorage.removeItem(PUBLIC_SESSION_KEY);
    clearPendingPublicSession();
    const pendingUser = buildSimulatedPublicSession(phone);
    savePendingPublicSession(pendingUser);
    setPublicUser(null);
    setPublicValidated(false);
    setPendingPublicUser(pendingUser);
    navigate("/verificar-codigo");
  };

  const handleRegisterPublicUser = (payload: RegisteredPublicUser) => {
    saveRegisteredUser(payload);
    const session = buildPublicSessionFromRegistered(payload);
    clearPendingPublicSession();
    setPendingPublicUser(null);
    setPublicUser(session);
    setPublicValidated(true);
    saveSession<PublicUserSession>(PUBLIC_SESSION_KEY, session);
    navigate("/inicio");
  };

  const handlePublicVerifySuccess = () => {
    const session = pendingPublicUser ?? loadPendingPublicSession();
    if (!session) {
      navigate("/", { replace: true });
      return;
    }

    saveSession<PublicUserSession>(PUBLIC_SESSION_KEY, session);
    clearPendingPublicSession();
    setPendingPublicUser(null);
    setPublicUser(session);
    setPublicValidated(true);
    navigate("/inicio", { replace: true });
  };

  const handleAddToCart = (payload: {
    id: number;
    name: string;
    unitPrice: number;
    quantity: number;
    icon?: string;
    image?: string;
  }) => {
    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === String(payload.id));
      if (existing) {
        return prev.map((item) =>
          item.id === String(payload.id)
            ? { ...item, quantity: item.quantity + payload.quantity }
            : item,
        );
      }
      return [
        ...prev,
        {
          id: String(payload.id),
          name: payload.name,
          quantity: payload.quantity,
          unitPrice: payload.unitPrice,
          icon: payload.icon,
          image: payload.image,
        },
      ];
    });
  };

  const handleRemoveCartItem = (itemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      return;
    }
    setIsCartOpen(false);
    navigate("/pedido");
  };

  const handleConfirmOrder = () => {
    setCartItems([]);
    setIsCartOpen(false);
  };

  const handlePublicDeliveryChange = (delivery: PublicDeliveryInfo) => {
    if (!publicUser) {
      return;
    }
    const updatedUser = {
      ...publicUser,
      address: delivery.address,
      phone: delivery.phone,
      reference: delivery.reference,
    };
    setPublicUser(updatedUser);
    saveSession<PublicUserSession>(PUBLIC_SESSION_KEY, updatedUser);

    const registered = loadRegisteredUsers()[publicUser.phone];
    if (registered) {
      saveRegisteredUser({
        ...registered,
        address: delivery.address,
        reference: delivery.reference,
      });
    }
  };

  const handlePublicLogout = () => {
    localStorage.removeItem(PUBLIC_SESSION_KEY);
    clearPendingPublicSession();
    setPublicUser(null);
    setPublicValidated(false);
    setPendingPublicUser(null);
    setCartItems([]);
    setIsCartOpen(false);
    navigate("/");
  };

  const activePendingPublicUser =
    pendingPublicUser ?? loadPendingPublicSession();

  const publicDeliveryInfo = useMemo<PublicDeliveryInfo | null>(() => {
    if (!publicUser) {
      return null;
    }
    return {
      address: publicUser.address,
      phone: publicUser.phone,
      reference: publicUser.reference ?? "",
    };
  }, [publicUser]);

  const cartUnits = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  useEffect(() => {
    if (!publicUser) {
      return;
    }

    if (
      publicUser.name === "Usuario" ||
      publicUser.address === "Direccion no registrada"
    ) {
      const normalized = buildSimulatedPublicSession(publicUser.phone);
      const currentReference = publicUser.reference ?? "";

      if (
        normalized.name !== publicUser.name ||
        normalized.address !== publicUser.address ||
        normalized.reference !== currentReference
      ) {
        setPublicUser(normalized);
        saveSession<PublicUserSession>(PUBLIC_SESSION_KEY, normalized);
      }
      return;
    }

    if (publicUser.reference === undefined) {
      const updatedUser = { ...publicUser, reference: "" };
      setPublicUser(updatedUser);
      saveSession<PublicUserSession>(PUBLIC_SESSION_KEY, updatedUser);
    }
  }, [publicUser]);

  return (
    <MedicinesCatalogProvider>
      <OrdersCatalogProvider>
        <PageMeta />
        <Routes>
          <Route
            path="/"
            element={
              publicValidated && publicUser ? (
                <Navigate to="/inicio" replace />
              ) : (
                <PublicAuthPage
                  onPhoneSubmit={handlePhoneSubmit}
                  onRegisterUser={handleRegisterPublicUser}
                />
              )
            }
          />
          <Route
            path="/verificar-codigo"
            element={
              activePendingPublicUser ? (
                <PublicVerifyCodePage
                  phone={activePendingPublicUser.phone}
                  onVerifySuccess={handlePublicVerifySuccess}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/inicio"
            element={
              publicValidated && publicUser && publicDeliveryInfo ? (
                <PublicHomePage
                  userName={publicUser.name}
                  deliveryInfo={publicDeliveryInfo}
                  cartItems={cartItems}
                  cartUnits={cartUnits}
                  isCartOpen={isCartOpen}
                  onOpenCart={() => setIsCartOpen(true)}
                  onCloseCart={() => setIsCartOpen(false)}
                  onRemoveCartItem={handleRemoveCartItem}
                  onDeliveryChange={handlePublicDeliveryChange}
                  onLogout={handlePublicLogout}
                  onCheckout={handleCheckout}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/productos"
            element={
              publicValidated && publicUser && publicDeliveryInfo ? (
                <PublicProductsPage
                  userName={publicUser.name}
                  deliveryInfo={publicDeliveryInfo}
                  cartItems={cartItems}
                  cartUnits={cartUnits}
                  isCartOpen={isCartOpen}
                  onOpenCart={() => setIsCartOpen(true)}
                  onCloseCart={() => setIsCartOpen(false)}
                  onRemoveCartItem={handleRemoveCartItem}
                  onAddToCart={handleAddToCart}
                  onDeliveryChange={handlePublicDeliveryChange}
                  onLogout={handlePublicLogout}
                  onCheckout={handleCheckout}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/categoria/:categoria"
            element={
              publicValidated && publicUser && publicDeliveryInfo ? (
                <PublicCategoryPage
                  deliveryInfo={publicDeliveryInfo}
                  cartItems={cartItems}
                  cartUnits={cartUnits}
                  isCartOpen={isCartOpen}
                  onOpenCart={() => setIsCartOpen(true)}
                  onCloseCart={() => setIsCartOpen(false)}
                  onAddToCart={handleAddToCart}
                  onRemoveCartItem={handleRemoveCartItem}
                  onDeliveryChange={handlePublicDeliveryChange}
                  onLogout={handlePublicLogout}
                  onCheckout={handleCheckout}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/pedido"
            element={
              publicValidated && publicUser && publicDeliveryInfo ? (
                <PublicCheckoutPage
                  userName={publicUser.name}
                  deliveryInfo={publicDeliveryInfo}
                  cartItems={cartItems}
                  cartUnits={cartUnits}
                  isCartOpen={isCartOpen}
                  onOpenCart={() => setIsCartOpen(true)}
                  onCloseCart={() => setIsCartOpen(false)}
                  onRemoveCartItem={handleRemoveCartItem}
                  onDeliveryChange={handlePublicDeliveryChange}
                  onLogout={handlePublicLogout}
                  onConfirmOrder={handleConfirmOrder}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/admin/login"
            element={
              adminUser ? (
                <Navigate to="/admin" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/admin/*"
            element={
              adminUser ? (
                <PrivateLayout user={adminUser} onLogout={handleLogout} />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </OrdersCatalogProvider>
    </MedicinesCatalogProvider>
  );
}
